/**
 * invoicing — every invoice a studio has out, and what is owed on it.
 *
 * INVOICE-1. Rennan: *"invoicing requires a complete new redesign and
 * build."* Surveying it first changed the question, because invoicing
 * already existed twice:
 *
 *   · a legacy `invoices` table that is one `amount`, a `due_date` and
 *     a `file_url` — a PDF made elsewhere and uploaded — reachable
 *     only from inside a project;
 *   · the Document Builder, where an invoice is a `documents` row of
 *     `kind = 'invoice'` with line-item, package and deposit blocks,
 *     a public `/d/:token` page, `total_cents`, and Stripe Connect,
 *     `payment_plans` and `stripe_payments` behind it.
 *
 * The second is the real one, so this reads that, and the gap it
 * fills is the one nobody had built: there was no org-wide view at
 * all. `useProjectDocuments` answers "what did this project invoice";
 * a photographer's question is "who hasn't paid me".
 *
 * WHAT IS DERIVED AND WHAT IS STORED
 *
 * `documents.paid_at` and `payment_status` exist, but what a studio
 * is owed is `total_cents` minus what actually landed, and what
 * landed is in `stripe_payments` — including partial payments, which
 * a single status column cannot express. So the amounts here are
 * summed from payments and the status is derived from the amounts.
 * A deposit paid on a 4,800 invoice makes it part-paid, not 'paid'
 * and not 'sent'.
 *
 * NO DUE DATE YET
 *
 * `documents` has no due date — `expires_at` is for the link, not for
 * the money — so an invoice cannot be overdue on its own terms until
 * 20261029160000_invoice_fields.sql is applied. INSTALLMENTS can:
 * `stripe_payments.scheduled_at` is when one is meant to be taken.
 * So `overdueCents` counts late installments today and late invoices
 * as well once the column exists, and `hasDueDates` says which world
 * we are in so the page can tell the truth rather than show an
 * always-empty bucket.
 */
import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useOrgId } from '@/hooks/useOrgId';
import { invoiceMath } from './invoiceTotals';
import type { InvoiceRow, InvoiceState } from './invoiceTotals';

// The arithmetic and its types live next door, with no imports, so
// the spec can run them. Re-exported so callers have one place to
// import from.
export { totalsFor, money, invoiceMath, type InvoiceRow, type InvoiceState, type MoneyTotals } from './invoiceTotals';

/** What this file reads of each table, and nothing more. */
interface DocRow {
  id: string;
  kind?: string | null;
  title?: string | null;
  org_id: string;
  client_id?: string | null;
  project_id?: string | null;
  total_cents?: number | null;
  payment_status?: string | null;
  status?: string | null;
  paid_at?: string | null;
  sent_at?: string | null;
  created_at: string;
  public_token?: string | null;
  /** Added by 20261029160000_invoice_fields.sql; absent until then. */
  invoice_number?: string | null;
  due_on?: string | null;
}

interface PaymentRow {
  id: string;
  document_id?: string | null;
  amount_cents?: number | null;
  amount_refunded_cents?: number | null;
  status?: string | null;
  paid_at?: string | null;
  scheduled_at?: string | null;
}

const toDate = (v: string | null | undefined): Date | null => (v ? new Date(v) : null);

/** A DATE column — "2026-10-29" — parsed as local, not as UTC midnight. */
const toDay = (v: string | null | undefined): Date | null => {
  if (!v) return null;
  const [y, m, d] = v.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

export interface InvoicingResult {
  invoices: InvoiceRow[];
  /** False until the migration lands; the page says so rather than
   *  showing an overdue bucket that can never fill. */
  hasDueDates: boolean;
}

export const useInvoicing = () => {
  const { data: orgId } = useOrgId();

  const documents = useQuery({
    queryKey: ['org-invoices', orgId],
    queryFn: async () => {
      if (!orgId) return [] as DocRow[];
      const { data, error } = await supabase
        .from('documents')
        .select('*')
        .eq('org_id', orgId)
        .eq('kind', 'invoice')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as DocRow[];
    },
    enabled: !!orgId,
  });

  const payments = useQuery({
    queryKey: ['org-invoice-payments', orgId],
    queryFn: async () => {
      if (!orgId) return [] as PaymentRow[];
      const { data, error } = await supabase
        .from('stripe_payments')
        .select('id, document_id, amount_cents, amount_refunded_cents, status, paid_at, scheduled_at')
        .eq('org_id', orgId);
      if (error) throw error;
      return (data ?? []) as unknown as PaymentRow[];
    },
    enabled: !!orgId,
  });

  const result = useMemo<InvoicingResult>(() => {
    const docs = documents.data ?? [];
    const pays = payments.data ?? [];
    const now = new Date();

    const byDoc = new Map<string, PaymentRow[]>();
    for (const p of pays) {
      if (!p.document_id) continue;
      const list = byDoc.get(p.document_id) ?? [];
      list.push(p);
      byDoc.set(p.document_id, list);
    }

    const hasDueDates = docs.some((d) => d.due_on !== undefined);

    const invoices = docs.map<InvoiceRow>((d) => {
      const mine = byDoc.get(d.id) ?? [];
      const paidCents = mine
        .filter((p) => p.status === 'paid' || !!p.paid_at)
        .reduce((sum, p) => sum + (p.amount_cents ?? 0) - (p.amount_refunded_cents ?? 0), 0);
      const totalCents = d.total_cents ?? 0;
      const dueCents = Math.max(0, totalCents - paidCents);

      // The soonest instalment that has not been taken. Payments with
      // no schedule are one-off charges and never "next".
      const nextDueAt = mine
        .filter((p) => !p.paid_at && p.status !== 'paid' && p.scheduled_at)
        .map((p) => new Date(p.scheduled_at as string))
        .sort((a, b) => a.getTime() - b.getTime())[0] ?? null;

      const dueOn = toDay(d.due_on);
      const settled = !!d.paid_at || (totalCents > 0 && dueCents === 0);
      const sent = !!d.sent_at || d.status === 'sent' || d.status === 'viewed';

      // Overdue beats part-paid: a studio chasing money wants the late
      // ones to look late even if something landed against them.
      const late =
        !settled &&
        ((dueOn !== null && dueOn < new Date(now.getFullYear(), now.getMonth(), now.getDate())) ||
          (nextDueAt !== null && nextDueAt < now));

      const state: InvoiceState = settled
        ? 'paid'
        : late
          ? 'overdue'
          : paidCents > 0
            ? 'part_paid'
            : sent
              ? 'sent'
              : 'draft';

      return {
        id: d.id,
        title: d.title ?? 'Invoice',
        number: d.invoice_number ?? null,
        clientId: d.client_id ?? null,
        clientName: null,
        totalCents,
        paidCents,
        dueCents,
        state,
        dueOn,
        nextDueAt,
        sentAt: toDate(d.sent_at),
        paidAt: toDate(d.paid_at),
        createdAt: new Date(d.created_at),
        publicToken: d.public_token ?? null,
        projectId: d.project_id ?? null,
      };
    });

    return { invoices, hasDueDates };
  }, [documents.data, payments.data]);

  return {
    ...result,
    isLoading: documents.isLoading || payments.isLoading,
    error: documents.error ?? payments.error ?? null,
  };
};

/* ────────────────────── writing an invoice ────────────────────── */

export interface InvoiceLine {
  /** Local id for React keys; never stored. */
  key: string;
  name: string;
  description?: string;
  quantity: number;
  unitCents: number;
}

export interface InvoiceDraft {
  title: string;
  clientId: string | null;
  projectId: string | null;
  invoiceNumber: string;
  issuedOn: string | null;
  dueOn: string | null;
  taxRate: number;
  notes: string;
  lines: InvoiceLine[];
  /** Percent of the total asked for up front. 0 = none. */
  depositPercent: number;
  /** An intake form to send them to as well. Null = none. */
  formId: string | null;
  /** Its name and public URL, resolved when the invoice is saved. */
  formLabel?: string;
  formUrl?: string;
}



/**
 * Save an invoice: the document row, and its line items as a SET.
 *
 * Not `useAddBlock` per row. A line-item table is edited as a whole —
 * rows are added, retyped and pulled out between one save and the
 * next — and ten rows through the per-block hooks is twenty round
 * trips with no transaction around them, where a half-failed save
 * leaves an invoice whose total disagrees with its lines.
 *
 * Only the three invoice block types are replaced. A document that
 * also carries a heading, a paragraph or a signature block — one made
 * in Contract Studio and opened here — keeps them.
 */
export const useSaveInvoice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, draft }: { id: string; draft: InvoiceDraft }) => {

      const blocks: { document_id: string; type: string; payload: unknown; sort_order: number }[] = [];
      draft.lines.forEach((l, i) => {
        blocks.push({
          document_id: id,
          type: 'lineitem',
          payload: {
            type: 'lineitem',
            name: l.name,
            description: l.description || undefined,
            price_cents: l.unitCents,
            quantity: l.quantity,
          },
          sort_order: i,
        });
      });
      // The amount to charge, as an EXACT number of cents.
      //
      // `stripe-create-document-checkout` computes the charge from the
      // LINE ITEMS — `chargeAmount = lineItemsTotalCents`, or a
      // percentage of it for a deposit block. Tax is not a line item,
      // so on an invoice with tax both of those are the wrong figure
      // and a studio would be paid the pre-tax amount without anything
      // saying so.
      //
      // `fixed_cents` is honoured exactly by that function, so the
      // block carries the number this editor worked out rather than a
      // percentage the server would recompute from a different base.
      // No deposit AND no tax needs no block at all: there the line
      // items ARE the total and the two agree.
      const { totalCents: charge, depositCents } = invoiceMath(draft);
      const dueNow = draft.depositPercent > 0 ? depositCents : charge;
      if (draft.depositPercent > 0 || draft.taxRate > 0) {
        blocks.push({
          document_id: id,
          type: 'deposit',
          payload: {
            type: 'deposit',
            fixed_cents: dueNow,
            // Both, on purpose, and they are read by different people.
            // The checkout function and the public page BOTH prefer
            // `fixed_cents` when it is positive, so the amount charged
            // is this editor's to the cent; `percentage` is what the
            // editor itself reads back to repopulate the field, which
            // it could not recover from an amount alone.
            ...(draft.depositPercent > 0 ? { percentage: draft.depositPercent } : {}),
            label: draft.depositPercent > 0 ? `Deposit · ${draft.depositPercent}%` : 'Pay invoice',
          },
          sort_order: blocks.length,
        });
      }
      if (draft.notes.trim()) {
        blocks.push({
          document_id: id,
          type: 'terms',
          payload: { type: 'terms', text: draft.notes.trim() },
          sort_order: blocks.length,
        });
      }
      if (draft.formId) {
        blocks.push({
          document_id: id,
          type: 'form_link',
          payload: {
            type: 'form_link',
            form_id: draft.formId,
            // Resolved here because the page that renders it is
            // anonymous and cannot look a form up by id — see the
            // note on the type.
            label: draft.formLabel || undefined,
            url: draft.formUrl || undefined,
          },
          sort_order: blocks.length,
        });
      }

      const { error: wipe } = await supabase
        .from('document_blocks')
        .delete()
        .eq('document_id', id)
        .in('type', ['lineitem', 'deposit', 'terms', 'form_link']);
      if (wipe) throw wipe;

      if (blocks.length) {
        const { error: add } = await supabase.from('document_blocks').insert(blocks as never);
        if (add) throw add;
      }

      // The five invoice columns land with 20261029160000, and this
      // runs before it does. Sent separately so a missing column
      // fails ONLY the extras: the lines, the total and the title are
      // saved either way, and the studio is told what did not stick
      // rather than losing the invoice they just typed.
      const core = { title: draft.title, client_id: draft.clientId, project_id: draft.projectId, total_cents: charge };
      const { error: coreErr } = await supabase
        .from('documents')
        .update({ ...core, updated_at: new Date().toISOString() } as never)
        .eq('id', id);
      if (coreErr) throw coreErr;

      const extras = {
        invoice_number: draft.invoiceNumber.trim() || null,
        issued_on: draft.issuedOn,
        due_on: draft.dueOn,
        tax_rate: draft.taxRate || null,
        invoice_notes: draft.notes.trim() || null,
      };
      const { error: extraErr } = await supabase
        .from('documents')
        .update(extras as never)
        .eq('id', id);

      return { fieldsMissing: !!extraErr };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['org-invoices'] });
      void queryClient.invalidateQueries({ queryKey: ['document'] });
      void queryClient.invalidateQueries({ queryKey: ['document-blocks'] });
      void queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
  });
};

/**
 * One project's invoices, from the system that is not the legacy one.
 *
 * INVOICE-6. A project's Finance tab and money strip read the old
 * `invoices` table only, so an invoice made in the new editor was
 * invisible inside the project it belonged to while the money page
 * counted it — the two disagreed, and a studio had no way to tell
 * which was lying.
 *
 * Drafts are left out on purpose. The money strip is what a studio is
 * OWED, and an invoice that has not been sent is not owed yet.
 *
 * This shares its queries with the money page through react-query, so
 * a project tab costs nothing extra once either has loaded.
 */
export const useProjectInvoices = (projectId: string | undefined) => {
  const { invoices, isLoading } = useInvoicing();
  return useMemo(() => {
    const mine = projectId
      ? invoices.filter((i) => i.projectId === projectId && i.state !== 'draft')
      : [];
    return {
      invoices: mine,
      totalCents: mine.reduce((sum, i) => sum + i.totalCents, 0),
      paidCents: mine.reduce((sum, i) => sum + i.paidCents, 0),
      isLoading,
    };
  }, [invoices, isLoading, projectId]);
};
